import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/types/database.types";

export interface AnalyticsData {
  leadsCollected: number;
  assistedCustomers: number;
  timeSaved: number;
  voiceCalls: number; // Changed from messages to voice calls
}

export interface TimeFrame {
  startDate: Date | null;
  endDate: Date;
}

export const analyticsService = {
  /**
   * Get analytics data for the organization based on timeframe
   */
  async getAnalytics(
    organizationId: string,
    timeFrame: "all" | "30d" = "30d",
  ): Promise<{
    data: AnalyticsData;
    error: Error | null;
  }> {
    try {
      const { startDate, endDate } = getTimeFrameDates(timeFrame);

      // Build base date filter
      const dateFilter = startDate ? startDate.toISOString() : undefined;

      // Get all conversations for the organization's agents
      const { data: agents } = await supabase
        .from("agents")
        .select("id")
        .eq("organization_id", organizationId);

      if (!agents || agents.length === 0) {
        return {
          data: {
            leadsCollected: 0,
            assistedCustomers: 0,
            timeSaved: 0,
            voiceCalls: 0,
          },
          error: null,
        };
      }

      const agentIds = agents.map((agent) => agent.id);

      // 1. Count leads (conversations where user_type = 'lead')
      let leadsQuery = supabase
        .from("conversations")
        .select("id", { count: "exact", head: true })
        .in("agent_id", agentIds)
        .eq("user_type", "lead");

      if (dateFilter) {
        leadsQuery = leadsQuery.gte("created_at", dateFilter);
      }

      const { count: leadsCount, error: leadsError } = await leadsQuery;

      if (leadsError) throw leadsError;

      // 2. Count assisted customers (conversations with estimated_time_saved > 0 or status indicating completion)
      let assistedQuery = supabase
        .from("conversations")
        .select("id", { count: "exact", head: true })
        .in("agent_id", agentIds)
        .or(
          "estimated_time_saved.gt.0,status.eq.completed,status.eq.resolved,state.eq.completed",
        );

      if (dateFilter) {
        assistedQuery = assistedQuery.gte("created_at", dateFilter);
      }

      const { count: assistedCount, error: assistedError } =
        await assistedQuery;

      if (assistedError) throw assistedError;

      // 3. Sum time saved from estimated_time_saved field
      let timeSavedQuery = supabase
        .from("conversations")
        .select("estimated_time_saved")
        .in("agent_id", agentIds)
        .not("estimated_time_saved", "is", null);

      if (dateFilter) {
        timeSavedQuery = timeSavedQuery.gte("created_at", dateFilter);
      }

      const { data: timeSavedData, error: timeSavedError } =
        await timeSavedQuery;

      if (timeSavedError) throw timeSavedError;

      const totalTimeSaved = (timeSavedData || []).reduce((sum, conv) => {
        return sum + (conv.estimated_time_saved || 0);
      }, 0);

      // 4. Count total messages
      const conversationIds = await getConversationIds(agentIds, dateFilter);

      let messagesQuery = supabase
        .from("messages")
        .select("id", { count: "exact", head: true });

      if (conversationIds.length > 0) {
        messagesQuery = messagesQuery.in("conversation_id", conversationIds);
      } else {
        // If no conversations, return 0 messages
        return {
          data: {
            leadsCollected: leadsCount || 0,
            assistedCustomers: assistedCount || 0,
            timeSaved: Math.round(totalTimeSaved * 10) / 10,
            voiceCalls: 0,
          },
          error: null,
        };
      }

      const { count: messagesCount, error: messagesError } =
        await messagesQuery;

      if (messagesError) throw messagesError;

      return {
        data: {
          leadsCollected: leadsCount || 0,
          assistedCustomers: assistedCount || 0,
          timeSaved: Math.round(totalTimeSaved * 10) / 10, // Round to 1 decimal place
          voiceCalls: voiceCallsCount || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      return {
        data: {
          leadsCollected: 0,
          assistedCustomers: 0,
          timeSaved: 0,
          messages: 0,
        },
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get analytics data for a specific agent based on timeframe
   */
  async getAgentAnalytics(
    agentId: string,
    timeFrame: "all" | "30d" = "30d",
  ): Promise<{
    data: AnalyticsData;
    error: Error | null;
  }> {
    try {
      const { startDate, endDate } = getTimeFrameDates(timeFrame);

      // Build base date filter
      const dateFilter = startDate ? startDate.toISOString() : undefined;

      // Use single agent ID instead of organization agents
      const agentIds = [agentId];

      // 1. Count leads (conversations where user_type = 'lead')
      let leadsQuery = supabase
        .from("conversations")
        .select("id", { count: "exact", head: true })
        .eq("agent_id", agentId)
        .eq("user_type", "lead");

      if (dateFilter) {
        leadsQuery = leadsQuery.gte("created_at", dateFilter);
      }

      const { count: leadsCount, error: leadsError } = await leadsQuery;

      if (leadsError) throw leadsError;

      // 2. Count assisted customers (conversations with estimated_time_saved > 0 or status indicating completion)
      let assistedQuery = supabase
        .from("conversations")
        .select("id", { count: "exact", head: true })
        .eq("agent_id", agentId)
        .or(
          "estimated_time_saved.gt.0,status.eq.completed,status.eq.resolved,state.eq.completed",
        );

      if (dateFilter) {
        assistedQuery = assistedQuery.gte("created_at", dateFilter);
      }

      const { count: assistedCount, error: assistedError } =
        await assistedQuery;

      if (assistedError) throw assistedError;

      // 3. Sum time saved from estimated_time_saved field
      let timeSavedQuery = supabase
        .from("conversations")
        .select("estimated_time_saved")
        .eq("agent_id", agentId)
        .not("estimated_time_saved", "is", null);

      if (dateFilter) {
        timeSavedQuery = timeSavedQuery.gte("created_at", dateFilter);
      }

      const { data: timeSavedData, error: timeSavedError } =
        await timeSavedQuery;

      if (timeSavedError) throw timeSavedError;

      const totalTimeSaved = (timeSavedData || []).reduce((sum, conv) => {
        return sum + (conv.estimated_time_saved || 0);
      }, 0);

      // 4. Count total messages
      const conversationIds = await getConversationIds(agentIds, dateFilter);

      let messagesQuery = supabase
        .from("messages")
        .select("id", { count: "exact", head: true });

      if (conversationIds.length > 0) {
        messagesQuery = messagesQuery.in("conversation_id", conversationIds);
      } else {
        // If no conversations, return 0 messages
        return {
          data: {
            leadsCollected: leadsCount || 0,
            assistedCustomers: assistedCount || 0,
            timeSaved: Math.round(totalTimeSaved * 10) / 10,
            voiceCalls: 0,
          },
          error: null,
        };
      }

      const { count: messagesCount, error: messagesError } =
        await messagesQuery;

      if (messagesError) throw messagesError;

      return {
        data: {
          leadsCollected: leadsCount || 0,
          assistedCustomers: assistedCount || 0,
          timeSaved: Math.round(totalTimeSaved * 10) / 10, // Round to 1 decimal place
          voiceCalls: voiceCallsCount || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error("Failed to fetch agent analytics:", error);
      return {
        data: {
          leadsCollected: 0,
          assistedCustomers: 0,
          timeSaved: 0,
          messages: 0,
        },
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get conversation IDs for message counting
   */
  async getConversationIds(
    agentIds: string[],
    dateFilter?: string,
  ): Promise<string[]> {
    return getConversationIds(agentIds, dateFilter);
  },
};

/**
 * Helper function to get date range for timeframe
 */
function getTimeFrameDates(timeFrame: "all" | "30d"): TimeFrame {
  const endDate = new Date();

  if (timeFrame === "all") {
    return { startDate: null, endDate };
  }

  // For 30d, go back 30 days
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);

  return { startDate, endDate };
}

/**
 * Get conversation IDs for a given set of agent IDs and optional date filter
 */
async function getConversationIds(
  agentIds: string[],
  dateFilter?: string,
): Promise<string[]> {
  let query = supabase
    .from("conversations")
    .select("id")
    .in("agent_id", agentIds);

  if (dateFilter) {
    query = query.gte("created_at", dateFilter);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching conversation IDs:", error);
    return [];
  }

  return (data || []).map((conv) => conv.id);
}
